<script setup lang="ts">
import type { TextFieldProps } from '@formwerk/core'
import type { CountryCode } from 'libphonenumber-js'
import { useFormContext, useTextField } from '@formwerk/core'
import { AsYouType } from 'libphonenumber-js'
import { DEFAULT_COUNTRY } from '#shared/utils/countries'

type PhoneFieldProps = Omit<TextFieldProps, 'type'> & {
  defaultCountry?: CountryCode
}

const props = defineProps<PhoneFieldProps>()

const { inputProps, labelProps, errorMessage, errorMessageProps, fieldValue, isBlurred, setValue } = useTextField({
  ...toRefs(props),
  schema: props.schema,
  type: 'tel',
})

const country = computed(() => props.defaultCountry ?? DEFAULT_COUNTRY)

function format(value: string) {
  return new AsYouType(country.value).input(value)
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement

  // Reformatting while deleting would put back the space the user just removed,
  // and while editing mid-string it would throw the caret to the end: both wait for blur.
  if ((event as InputEvent).inputType?.startsWith('delete') || input.selectionStart !== input.value.length)
    return

  applyFormatted(input)
}

function onBlur(event: FocusEvent) {
  applyFormatted(event.target as HTMLInputElement)
}

// AsYouType drops non-digits, so typing letters can format to ''. When the model already held ''
// Vue skips the DOM patch and the letters would stay visible while the field counts as empty:
// write the formatted value to the input as well.
function applyFormatted(input: HTMLInputElement) {
  const formatted = format(input.value)
  if (formatted === input.value)
    return

  input.value = formatted
  setValue(formatted)
}

// Shows which prefix the number is being read with, so a missing "+" doesn't go unnoticed.
// const detected = computed(() => {
//   if (!fieldValue.value)
//     return

//   const typer = new AsYouType(country.value)
//   typer.input(fieldValue.value)

//   const callingCode = typer.getCallingCode() ?? (fieldValue.value.trim().startsWith('+') ? undefined : getCountryCallingCode(country.value))
//   if (!callingCode)
//     return

//   // Until the number is long enough to tell, a shared code (+1, +44, …) only names a country
//   // when it's the default one or the only one using it.
//   const candidates = getCountries().filter(code => getCountryCallingCode(code) === callingCode)
//   const code = typer.getCountry()
//     ?? (candidates.includes(country.value) ? country.value : candidates.length === 1 ? candidates[0] : undefined)

//   return {
//     callingCode: `+${callingCode}`,
//     name: COUNTRIES.find(c => c.code === code)?.name,
//   }
// })

const isDirty = computed(() => fieldValue.value?.length > 0)
// Inside a form, errors only show after a submit attempt, then update live as the user fixes them
const form = useFormContext()
const errored = computed(() => (form ? form.isSubmitAttempted.value : isBlurred.value) && errorMessage.value)
</script>

<template>
  <div class="input">
    <div class="w-full relative" :class="{ 'c-red': errored }">
      <input
        v-bind="inputProps"
        placeholder=" "
        inputmode="tel"
        class="peer typo-sans-1 c-current leading-5 pb-0.75 pt-3 appearance-none border-b border-current h-9 w-full block uppercase"
        @input="onInput"
        @blur="onBlur"
      >

      <label
        v-bind="labelProps"
        class="typo-sans-1 leading-5 motion-snug pointer-events-none select-none uppercase left-0 top-3 absolute peer-focus:scale-80 peer-focus:-translate-x-10% peer-focus:-translate-y-4"
        :class="{
          '-translate-y-4 scale-80 -translate-x-10%': isDirty,
        }"
      >
        {{ label }}
      </label>

      <!-- <TransitionDefault>
        <span
          v-if="detected"
          class="typo-sans-1 opacity-50 pointer-events-none select-none uppercase bottom-0 right-0 absolute"
          aria-live="polite"
        >
          {{ detected.callingCode }}<template v-if="detected.name"> · {{ detected.name }}</template>
        </span>
      </TransitionDefault> -->
    </div>
    <TransitionDefault>
      <p
        v-if="errored"
        v-bind="errorMessageProps"
        class="text-red-500 typo-sans-1 mt-1"
      >
        {{ errorMessage }}
      </p>
    </TransitionDefault>
  </div>
</template>
